import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../modules/service/auth.service';

export type OtpMethod = 'email' | 'phone' | 'app';

@Component({
  selector: 'app-holl-otp',
  templateUrl: './holl-otp.component.html',
  styleUrls: ['./holl-otp.component.scss']
})
export class HollOtpComponent implements OnInit, OnDestroy {

  @ViewChildren('otpBox') otpBoxes: QueryList<ElementRef>;

  selectedMethod: OtpMethod = 'email';

  // Fixed array of 6 slot indices — never replaced, used only for *ngFor iteration
  readonly slots = [0, 1, 2, 3, 4, 5];

  // 6 individual digit slots — mutated in place; we never swap the array reference
  digits: string[] = ['', '', '', '', '', ''];

  isVerifying    = false;
  isResending    = false;
  isSending      = false;
  codeSent       = false;
  errorMessage   = '';
  successMessage = '';

  // Countdown timer for resend (5 minutes)
  resendCountdown = 300;
  private countdownInterval: any;

  // Email received from the login page via router state
  userEmail = '';

  // Masked display values — populated from router state once we have the email
  maskedEmail = '';
  maskedPhone = '';

  constructor(private router: Router, private auth: AuthService) {
    // Read state passed from the login page via router navigation extras.
    var nav = this.router.getCurrentNavigation();
    var state = nav && nav.extras && nav.extras.state
      ? nav.extras.state as { email?: string; identifier?: string }
      : null;

    if (state && state.email) {
      this.userEmail   = state.email;
      this.maskedEmail = this.maskEmail(state.email);
    }

    // identifier may be the phone number typed at login — use it for maskedPhone
    if (state && state.identifier) {
      var id = state.identifier;
      var looksLikePhone = /^[+\d]/.test(id) && id.replace(/\D/g, '').length >= 7;
      this.maskedPhone = looksLikePhone ? this.maskPhone(id) : this.maskedPhone;
    }
  }

  ngOnInit() {
    // If there is no email (e.g. user navigated directly), go back to login
    if (!this.userEmail) {
      this.router.navigate(['']);
    }
  }

  ngOnDestroy() {
    clearInterval(this.countdownInterval);
  }

  selectMethod(method: OtpMethod) {
    this.selectedMethod  = method;
    this.clearDigits();
    this.errorMessage    = '';
    this.successMessage  = '';
    this.codeSent        = false;
    this.isSending       = false;
    clearInterval(this.countdownInterval);
    this.resendCountdown = 300;

    if (method === 'app') {
      var self = this;
      setTimeout(function() { self.focusBox(0); }, 80);
    }
  }

  // ── Send initial OTP code ─────────────────────────────────────────────────

  sendCode() {
    if (this.selectedMethod === 'app') {
      // Authenticator app — no server request needed, just show the inputs
      this.codeSent = true;
      this.successMessage = 'Open your authenticator app for the current code.';
      var self = this;
      setTimeout(function() { self.successMessage = ''; }, 4000);
      this.startCountdown();
      return;
    }

    this.isSending    = true;
    this.errorMessage = '';

    var channel = this.selectedMethod as 'email' | 'phone';
    this.auth.requestMfa({ email: this.userEmail, channel: channel }).subscribe(
      (res) => {
        this.isSending = false;

        if (res.responseCode === '000') {
          this.codeSent       = true;
          this.successMessage = res.responseMessage ||
            ('Code sent to ' + (this.selectedMethod === 'email' ? this.maskedEmail : (this.maskedPhone || 'your registered mobile number')));
          var self = this;
          setTimeout(function() { self.successMessage = ''; }, 4000);
          this.startCountdown();
        } else {
          this.errorMessage = res.responseMessage || 'Failed to send code. Please try again.';
        }
      },
      (err) => {
        this.isSending = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Failed to send code. Please check your connection and try again.';
      }
    );
  }

  // ── OTP input handling ────────────────────────────────────────────────────

  trackByIndex(index: number): number { return index; }

  onDigitInput(event: any, index: number) {
    var input = event.target as HTMLInputElement;
    var raw   = input.value.replace(/\D/g, '');
    var digit = raw.length > 0 ? raw.charAt(raw.length - 1) : '';

    this.digits[index] = digit;
    input.value        = digit;

    if (digit && index < 5) { this.focusBox(index + 1); }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = '';
        this.syncBoxValue(index);
      } else if (index > 0) {
        this.digits[index - 1] = '';
        this.syncBoxValue(index - 1);
        this.focusBox(index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft'  && index > 0) { this.focusBox(index - 1); }
    if (event.key === 'ArrowRight' && index < 5) { this.focusBox(index + 1); }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    var clip = event.clipboardData || (window as any).clipboardData;
    var text = clip.getData('text').replace(/\D/g, '').slice(0, 6);
    for (var i = 0; i < 6; i++) {
      this.digits[i] = i < text.length ? text.charAt(i) : '';
      this.syncBoxValue(i);
    }
    this.focusBox(Math.min(text.length, 5));
  }

  private syncBoxValue(index: number) {
    if (!this.otpBoxes) { return; }
    var arr = this.otpBoxes.toArray();
    if (arr[index]) { arr[index].nativeElement.value = this.digits[index]; }
  }

  private focusBox(index: number) {
    if (!this.otpBoxes) { return; }
    var arr = this.otpBoxes.toArray();
    if (arr[index]) {
      arr[index].nativeElement.focus();
      arr[index].nativeElement.select();
    }
  }

  private clearDigits() {
    for (var i = 0; i < 6; i++) {
      this.digits[i] = '';
      this.syncBoxValue(i);
    }
  }

  get otpValue(): string { return this.digits.join(''); }

  get otpComplete(): boolean {
    for (var i = 0; i < this.digits.length; i++) {
      if (!this.digits[i]) { return false; }
    }
    return this.digits.length === 6;
  }

  // ── Verify OTP ────────────────────────────────────────────────────────────

  verify() {
    this.errorMessage = '';

    if (!this.otpComplete) {
      this.errorMessage = 'Please enter all 6 digits.';
      return;
    }

    this.isVerifying = true;

    this.auth.verifyMfa({ email: this.userEmail, otp: this.otpValue }).subscribe(
      (res) => {
        this.isVerifying = false;

        if (res.responseCode === '000') {
          // Session already saved inside AuthService.verifyMfa()
          this.successMessage = 'Verification successful! Redirecting…';
          var self = this;
          var isDefaultPassword = res.user && res.user.defaultPassword === true;
          setTimeout(function() {
            if (isDefaultPassword) {
              self.router.navigate(['change-password'], { state: { email: self.userEmail } });
            } else {
              self.router.navigate(['products']);
            }
          }, 1000);
        } else {
          // Map known error codes to friendly messages
          if (res.responseCode === '408') {
            this.errorMessage = 'Your code has expired. Please request a new one.';
          } else if (res.responseCode === '401') {
            this.errorMessage = 'Invalid code. Please try again.';
          } else {
            this.errorMessage = res.responseMessage || 'Verification failed. Please try again.';
          }
          this.clearDigits();
          this.focusBox(0);
        }
      },
      (err) => {
        this.isVerifying  = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Verification failed. Please check your connection and try again.';
        this.clearDigits();
        this.focusBox(0);
      }
    );
  }

  // ── Resend ────────────────────────────────────────────────────────────────

  resend() {
    if (this.resendCountdown > 0 || this.isResending) { return; }
    this.isResending  = true;
    this.errorMessage = '';

    var resendChannel = this.selectedMethod as 'email' | 'phone';
    this.auth.requestMfa({ email: this.userEmail, channel: resendChannel }).subscribe(
      (res) => {
        this.isResending = false;

        if (res.responseCode === '000') {
          this.successMessage = 'A new code has been sent.';
          var self = this;
          setTimeout(function() { self.successMessage = ''; }, 3000);
          this.resetCountdown();
        } else {
          this.errorMessage = res.responseMessage || 'Failed to resend. Please try again.';
        }
      },
      (err) => {
        this.isResending  = false;
        this.errorMessage = err && err.error && err.error.responseMessage
          ? err.error.responseMessage
          : 'Failed to resend. Please try again.';
      }
    );
  }

  private startCountdown() {
    this.countdownInterval = setInterval(() => {
      if (this.resendCountdown > 0) { this.resendCountdown--; }
      else { clearInterval(this.countdownInterval); }
    }, 1000);
  }

  private resetCountdown() {
    clearInterval(this.countdownInterval);
    this.resendCountdown = 300;
    this.startCountdown();
  }

  goBack() { this.router.navigate(['']); }

  changeMethod() {
    clearInterval(this.countdownInterval);
    this.codeSent        = false;
    this.isSending       = false;
    this.clearDigits();
    this.errorMessage    = '';
    this.successMessage  = '';
    this.resendCountdown = 300;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** Mask an email address: e.ofori@example.com → e***i@example.com */
  private maskEmail(email: string): string {
    var parts = email.split('@');
    if (parts.length !== 2) { return email; }
    var local  = parts[0];
    var domain = parts[1];
    if (local.length <= 2) { return local + '@' + domain; }
    return local.charAt(0) + '***' + local.charAt(local.length - 1) + '@' + domain;
  }

  /** Mask a phone number: +233543016533 → +233 *** 6533 */
  private maskPhone(phone: string): string {
    var digits = phone.replace(/\D/g, '');
    if (digits.length < 4) { return phone; }
    var last4  = digits.slice(-4);
    var prefix = phone.startsWith('+') ? '+' + digits.slice(0, digits.length - 4 > 3 ? 3 : digits.length - 4) : digits.slice(0, digits.length - 4 > 3 ? 3 : digits.length - 4);
    return prefix + ' *** ' + last4;
  }
}
